import { Injectable } from '@angular/core';
import { forkJoin, from, Observable, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import {
  assignMergedFieldStrings,
  customFields,
  customFieldsDefault,
  FieldTitlesByTopicType,
} from '../constants/defaultFieldTitlesFromBE';
import { BuildProps } from '@app/origami-api/build-props';
import { Locale } from '@app/origami-api/constants/default-locales';

/**
 * Loads merged system+template default field titles as static JSON from the host
 * (`fields/system`, `fields/template`). Uses `XMLHttpRequest` instead of `HttpClient`/`ServiceBase`
 * because these URLs are unauthenticated static assets on `BuildProps.origin`, not Origami WCF
 * endpoints (no CAS flow, no API response envelope).
 */
@Injectable()
export class DefaultFieldTitlesService {
  private readonly availableLocales = Object.values(Locale);

  loadFieldTitles(locale: string): Observable<void> {
    if (!this.availableLocales.includes(locale as Locale)) {
      locale = Locale.en_US;
    }

    // Load English defaults: system + template, then locale-specific: system + template
    return forkJoin([
      this.fetchJson(`${BuildProps.origin}fields/system/?loc=${Locale.en_US}&nocache=${Date.now()}`),
      this.fetchJson(`${BuildProps.origin}fields/template/?loc=${Locale.en_US}&nocache=${Date.now()}`),
    ]).pipe(
      switchMap(([engSystem, engTemplate]) => {
        assignMergedFieldStrings(customFieldsDefault, engSystem, engTemplate);
        (tiStrings.shared.defaultFieldTitlesDefault as any) = customFieldsDefault;

        if (locale === Locale.en_US) {
          assignMergedFieldStrings(customFields, engSystem, engTemplate);
          (tiStrings.shared.defaultFieldTitles as any) = customFields;
          return of(undefined);
        }

        return forkJoin([
          this.fetchJson(`${BuildProps.origin}fields/system/?loc=${locale}&nocache=${Date.now()}`),
          this.fetchJson(`${BuildProps.origin}fields/template/?loc=${locale}&nocache=${Date.now()}`),
        ]).pipe(
          map(([locSystem, locTemplate]) => {
            assignMergedFieldStrings(customFields, locSystem, locTemplate);
            (tiStrings.shared.defaultFieldTitles as any) = customFields;
          }),
        );
      }),
      catchError((error) => {
        // console.warn('loadFieldTitles failed', error);
        return of(undefined);
      }),
    );
  }

  private fetchJson(url: string): Observable<FieldTitlesByTopicType> {
    return from(
      new Promise<FieldTitlesByTopicType>((resolve, reject) => {
        const req = new XMLHttpRequest();
        req.addEventListener('load', () => {
          if (req.status === 200) {
            try {
              const res = req.responseText ? JSON.parse(req.responseText) : {};
              resolve(res);
            } catch (error) {
              reject(new Error('Failed to parse JSON response: ' + error.message));
            }
          } else {
            reject(new Error('Request failed with status: ' + req.status));
          }
        });

        req.addEventListener('error', () => {
          reject(new Error('Network error occurred'));
        });

        req.open('GET', url);
        req.send();
      }),
    ).pipe(
      catchError((error) => {
        // console.warn('fetchJson failed for ' + url, error);
        return of({});
      }),
    );
  }
}
